import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { allCountries, CanadaProvinces, USAStates, MexicoStates } from '../../utils/dropOptions';

function UploadFiles({ onNext }) {
    const [files, setFiles] = useState({
        license: {
            file: null,
            number: '',
            authority: '',
            country: '',
            state: '',
            class: '',
            expiration: null,
        },
        passport: {
            file: null,
            issuingCountry: '',
            number: '',
            expiry: null,
        },
        prCard: {
            file: null,
            number: '',
        },
        workPermit: {
            file: null,
        }
    });

    const handleFileChange = (type) => (event) => {
        setFiles({
            ...files,
            [type]: {
                ...files[type],
                file: event.target.files[0] || null
            }
        });
    };

    const handleFieldChange = (type, field) => (event) => {
        setFiles({
            ...files,
            [type]: {
                ...files[type],
                [field]: event.target.value
            }
        });
    };

    const handleDateChange = (type, field) => (date) => {
        setFiles({
            ...files,
            [type]: {
                ...files[type],
                [field]: date
            }
        });
    };

    const getStateOptions = (country) => {
        switch (country) {
            case 'Canada':
                return CanadaProvinces;
            case 'U.S.A':
                return USAStates;
            case 'Mexico':
                return MexicoStates;
            default:
                return [];
        }
    };

    const handleSubmit = () => {
        // Add validation if needed
        onNext(files);
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Typography variant="h6" gutterBottom>Upload Required Files</Typography>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>License (Required)</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth>
                                Upload License
                                <input type="file" hidden onChange={handleFileChange('license')} />
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {files.license.file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>{files.license.file.name}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="License Number"
                                value={files.license.number}
                                onChange={handleFieldChange('license', 'number')}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Issuing Authority"
                                value={files.license.authority}
                                onChange={handleFieldChange('license', 'authority')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Country</InputLabel>
                                <Select
                                    value={files.license.country}
                                    label="Country"
                                    onChange={handleFieldChange('license', 'country')}
                                >
                                    {allCountries.map((country) => (
                                        <MenuItem key={country} value={country}>{country}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={!files.license.country}>
                                <InputLabel>State/Province</InputLabel>
                                <Select
                                    value={files.license.state}
                                    label="State/Province"
                                    onChange={handleFieldChange('license', 'state')}
                                >
                                    {getStateOptions(files.license.country).map((state) => (
                                        <MenuItem key={state} value={state}>{state}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Class"
                                value={files.license.class}
                                onChange={handleFieldChange('license', 'class')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Expiration Date"
                                    value={files.license.expiration}
                                    onChange={handleDateChange('license', 'expiration')}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Passport</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth>
                                Upload Passport
                                <input type="file" hidden onChange={handleFileChange('passport')} />
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {files.passport.file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>{files.passport.file.name}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Issuing Country</InputLabel>
                                <Select
                                    value={files.passport.issuingCountry}
                                    label="Issuing Country"
                                    onChange={handleFieldChange('passport', 'issuingCountry')}
                                >
                                    {allCountries.map((country) => (
                                        <MenuItem key={country} value={country}>{country}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Passport Number"
                                value={files.passport.number}
                                onChange={handleFieldChange('passport', 'number')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Expiry Date"
                                    value={files.passport.expiry}
                                    onChange={handleDateChange('passport', 'expiry')}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>PR Card</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth>
                                Upload PR Card
                                <input type="file" hidden onChange={handleFileChange('prCard')} />
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {files.prCard.file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>{files.prCard.file.name}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="PR Card Number"
                                value={files.prCard.number}
                                onChange={handleFieldChange('prCard', 'number')}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Work Permit</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth>
                                Upload Work Permit
                                <input type="file" hidden onChange={handleFileChange('workPermit')} />
                            </Button>
                            {files.workPermit.file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>{files.workPermit.file.name}</Typography>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit}
                >
                    Finish
                </Button>
            </Box>
        </Box>
    );
}

export default UploadFiles;
